/* eslint-disable import/no-anonymous-default-export */
export default {
  getHost() {
    switch (process.env.REACT_APP_ENVIRONMENT) {
      case "PRODUCTION":
        return process.env.REACT_APP_PRODUCTIONURL;
      case "TEST":
        return process.env.REACT_APP_TESTURL;
      default:
        return (
          process.env.REACT_APP_DEVELOPMENTURL || "http://localhost/Accountify/"
        );
    }
  },

  // ── COOKIE HELPERS ──────────────────────────────────────────────────────────
  getCookie(name) {
    const match = document.cookie.match(
      new RegExp("(^| )" + name + "=([^;]+)"),
    );
    return match ? decodeURIComponent(match[2]) : null;
  },

  setCookie(name, value, days = 1) {
    const expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)};path=/;expires=${expires};SameSite=Strict`;
  },

  deleteCookie(name) {
    document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;SameSite=Strict`;
  },

  parseCookieValue(name) {
    const raw = this.getCookie(name);
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return raw;
    }
  },

  // ── JWT ─────────────────────────────────────────────────────────────────────
  parseJwt(token) {
    try {
      const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const json = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      return JSON.parse(json);
    } catch {
      return null;
    }
  },

  // ── SESSION MANAGEMENT ────────────────────────────────────────────────────
  setSession(token, userData) {
    this.setCookie("auth_token", token, 7);
    this.setCookie("_get_me_weepy", JSON.stringify(userData), 7);
  },

  /**
   * Session comes from this system, not the one these pages were written in.
   *
   * They arrived expecting an "auth_token" cookie and a "_get_me_weepy" cookie
   * holding the user. Here the whole session is one JWT in localStorage under
   * "projectm@user", with the user inside its `data` claim. Reading the host's
   * session rather than porting a second one means there is only ever one place
   * a user is logged in, and logging out of the system logs them out of
   * accounting too.
   */
  getToken() {
    return localStorage.getItem("projectm@user") || null;
  },

  getUserData() {
    const token = this.getToken();
    if (!token) return null;
    const payload = this.parseJwt(token);
    const data = payload && payload.data ? payload.data : null;
    if (!data) return null;
    // Normalised to the shape the accounting pages expect.
    return { ...data, id: data.user_id, user_id: data.user_id };
  },

  isLoggedIn() {
    // 1️⃣ Primary check: Does user data exist? (set by frontend after login)
    const userData = this.getUserData();
    if (userData && (userData.user_id || userData.id)) {
      return true;
    }

    // 2️⃣ Fallback check: Does token exist and is it valid? (if not HttpOnly)
    const token = this.getToken();
    if (token) {
      const payload = this.parseJwt(token);
      // Check expiry if present
      if (payload && (!payload.exp || payload.exp > Date.now() / 1000)) {
        return true;
      }
    }

    return false;
  },

  isTokenValid() {
    const token = this.getToken();
    if (!token) return false;
    const payload = this.parseJwt(token);
    return payload && (!payload.exp || payload.exp > Date.now() / 1000);
  },

  clearAuth() {
    this.deleteCookie("auth_token");
    this.deleteCookie("_get_me_weepy");
    // Clear any legacy localStorage leftovers
    [
      "accountify@user_id",
      "accountify@role_id",
      "accountify@username",
      "accountify@is_secure",
      "accountify@setup_done",
      "accountify@token",
    ].forEach((k) => {
      localStorage.removeItem(k);
    });
  },

  sessionGuard() {
    return this.isLoggedIn();
  },

  isSecure() {
    const authData = this.getUserData();
    if (!authData) return false;
    const secure = authData.is_secure;
    return secure === "1" || secure === 1 || secure === true;
  },

  isSetupDone() {
    const authData = this.getUserData();
    if (!authData) return false;
    return (
      authData.setup_done === "1" ||
      authData.setup_done === 1 ||
      authData.setup_done === true
    );
  },

  markSecure() {
    const authData = this.getUserData() || {};
    authData.is_secure = 1;
    this.setCookie("_get_me_weepy", JSON.stringify(authData), 7);
  },

  markSetupDone() {
    const authData = this.getUserData() || {};
    authData.setup_done = 1;
    this.setCookie("_get_me_weepy", JSON.stringify(authData), 7);
  },

  getUserId() {
    const authData = this.getUserData();
    return authData?.user_id || authData?.id || null;
  },

  getRoleId() {
    const authData = this.getUserData();
    return authData?.role_id || null;
  },

  getUsername() {
    const authData = this.getUserData();
    return authData?.username || "";
  },

  // ── OTHER UTILITIES ──────────────────────────────────────────────────────
  findInObject(arr, code) {
    if (!arr || !code) return false;
    return arr.some((item) => item.code === code || item === code);
  },

  formatCurrency(amount, symbol = "UGX") {
    if (amount === null || amount === undefined) return `${symbol} 0.00`;
    return `${symbol} ${parseFloat(amount).toLocaleString("en-UG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  },

  formatDate(dateStr) {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  },

  toSelectOptions(arr, valueKey = "id", labelKey = "name") {
    if (!Array.isArray(arr)) return [];
    return arr.map((item) => ({
      value: item[valueKey],
      label: item[labelKey],
    }));
  },

  fromSelectOption(opt) {
    return opt ? opt.value : null;
  },
};
